// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { Query } from 'urql';

import { localize, unescaped } from '@ta/common.i18n';
import type { ContentProps, InternalProps } from './base';
import { DefaultProps, defaultConnect } from './base';
import ReportIAPPanel, { alwaysNo } from '../ReportIAPPanel';

import { ContentType } from '../submit/SubmitIapVote';
import IsOwnerQuery from './IsPropertyOwner.gql';
import styles from './Reviews.less';

export type LocationInfo = {|
  placeType?: string,
  locationId?: number,
|};

type ReviewProps = {
  ...ContentProps,
  location?: LocationInfo,
};
type InternalReviewProps = {
  ...ReviewProps,
  ...InternalProps,
};

const renderOwnerFlow = (props: InternalReviewProps, location, locationId, isNonHotel: boolean) => {
  const { ownerManagementUrl, ownerResponseUrl } = location;
  const ownerSupportUrl = `/OwnerSupport?locationid=${locationId}&category=4&topic=44`;
  const removeListingUrl =
    'https://www.tripadvisorsupport.com/hc/en-us/articles/200614617-As-a-business-owner-or-representative-how-do-I-remove-my-listing-';

  const nonHotelUpdateListingLink = unescaped(
    localize('report_review_redirect_2_part_2', {
      sUrl: `/UpdateListing-d${locationId}`,
    }),
  ); // To edit or improve your business details please go to the <a href="{sUrl}">Improve this Listing</a> link.
  const hotelUpdateListingLink = unescaped(
    localize('report_review_redirect_2_part_1', {
      /* $FlowFixGraphql (WP-1357) */
      sUrl: ownerManagementUrl,
    }),
  ); // To edit or improve your business details please visit your <a href="{sUrl}">Management Center</a>
  const updateDetailsLink = isNonHotel ? nonHotelUpdateListingLink : hotelUpdateListingLink;

  const reportClosedLink = unescaped(
    /* $FlowFixGraphql (WP-1357) */
    localize('report_review_redirect_3', { sUrl: ownerManagementUrl }),
  ); // To report your business as closed please visit your <a href="{sUrl}">Management Center</a>

  const editResponseLink = unescaped(
    localize('report_review_redirect_4_ver_2', {
      /* $FlowFixGraphql (WP-1357) */
      sUrl: ownerResponseUrl,
    }),
  ); // To edit your response, first remove it then submit a new one. Visit the <a href="{sUrl}">Respond to Reviews</a> section in the Management Center, then click 'delete response' at the bottom.

  const ownerChangedLink = unescaped(
    localize('report_review_redirect_5', { sUrl: ownerSupportUrl }),
  ); // To report a change of ownership or renovation please visit your <a href="{sUrl}">Business Representative Support Page</a> in your Management Center.

  const reportPhotoText = localize('report_review_redirect_6'); // To report a photo please navigate to the photo at the top of the page and submit a report via the report flag.

  const removeListingLink = unescaped(
    localize('report_review_redirect_7', {
      sUrl: removeListingUrl,
      sEmphClass: styles.bold,
    }),
  ); // We do not remove listings unless a business is <span class="{sEmphClass}">permanently closed</span>. For further information see our <a href="{sUrl}">Help Center</a>.

  return (
    <ReportIAPPanel
      csrfToken={props.csrfToken}
      userId={props.userId}
      // $FlowFixMe contentId is fine, I'm not sure what you're so mad about.
      contentId={props.contentId}
      contentType={ContentType.USERREVIEW}
      onError={props.onError}
      onComplete={props.onComplete}
      repostInfo={props.repostInfo}
    >
      {panel => {
        const reportReviewSubPanelId = 'ownerReportReview';

        return panel.isSubPanel(reportReviewSubPanelId) ? (
          <div key={reportReviewSubPanelId}>
            {panel.createRadioWithSuggestionBox(
              'WRONG_LOCATION',
              localize('report_review_radio_desc_19'), // Do you know where this review belongs?
            )}
            {panel.createRadioWithOptionalComment(
              'INAPPROPRIATE',
              localize('report_review_radio_desc_9'), // It contains profanity, sexually explicit comments, hate speech, prejudice, threats, or personal insults.
            )}
            {panel.createRadioWithRequiredComment(
              'WRITTEN_BY_COMPETITOR',
              localize('report_review_radio_desc_10'), // I believe an employee of a competing business or former employee of my business wrote this review.
            )}
            {panel.createRadioWithOptionalComment(
              'MORE_THAN_1_YEAR_AGO',
              localize('report_review_radio_desc_11_ver_1'), // While reviewers may only write about an experience that happened within the past year, we do not remove old reviews.Older reviews have less impact on the Popularity Index.
            )}
            {panel.createRadioWithOptionalComment(
              'REVIEW_NOT_CONSISTENT_WITH_RATING',
              `${localize('report_review_radio_desc_12_part_1')} ${localize(
                'report_review_radio_desc_12_part_2',
              )}`, // Example: The review is titled "Excellent" but the overall rating is 1 The reviewer rated a category they did not experience. Example: "Sleep quality:2" but reviewer did not stay. Overall ratings are NOT an average of sub-ratings.
            )}
            {panel.createRadioWithOptionalComment(
              'NOT_TRAVELERS_OWN',
              localize('report_review_radio_desc_13_ver_2'), // This reviewer had no experience of my business including the reservation process or check-in. (TripAdvisor does allow reviews of incidents/events that occur during the reservation process, check-in or upon arrival at the establishment.)
            )}
            {panel.createRadioWithOptionalComment(
              'DUPLICATE_CONTENT',
              `${localize('report_review_radio_desc_14_part_1')} ${localize(
                'report_review_radio_desc_14_part_2',
              )}`, // This person has 2 reviews with the same text, or copied text from another reviewer or source. Reviewers are allowed to write one review of each experience of a business within our timeline guidelines.
            )}
            {panel.createRadioWithOptionalComment(
              'PROMOTIONAL',
              localize('report_review_radio_desc_15_ver_1'), // It contains advertising or promotions for another business.
            )}
            {panel.createRadioWithOptionalComment(
              'NOT_ORIGINAL_REVIEW',
              localize('report_review_radio_desc_16'), // Per our guidelines, each reviewer may only submit one review of an experience. But multiple travelers are entitled to write a review of the same experience and those reviews will not be removed if they meet guidelines.
            )}
            {panel.createRadioWithContentNoSubmit(
              'DISAGREE_WITH_INFO',
              /* $FlowFixGraphql (WP-1357) */
              unescaped(localize('report_review_radio_desc_17', { sUrl: ownerResponseUrl })), // We do not arbitrate factual disputes with reviewers. If you disagree with the content of a review please share your side of the story with a <a href="{sUrl}">Management Response</a>.
            )}
            {panel.createRadioWithOptionalComment(
              'CONTAINS_PRIVATE_INFO',
              localize('report_review_radio_desc_18'), // It contains information such as full names, email addresses, credit cards or passwords.
            )}
            {panel.createRadioWithRequiredComment('OTHER')}
          </div>
        ) : (
          <React.Fragment>
            {panel.createRadioButtonWithSubPanel(
              'REPORT_THIS_REVIEW',
              alwaysNo,
              reportReviewSubPanelId,
            )}
            {panel.createRadioWithContentNoSubmit('UPDATE_BUSINESS_DETAILS', updateDetailsLink)}
            {panel.createRadioWithContentNoSubmit('REPORT_BUSINESS_CLOSED', reportClosedLink)}
            {panel.createRadioWithContentNoSubmit('EDIT_REMOVE_RESPONSE', editResponseLink)}
            {panel.createRadioWithContentNoSubmit('REPORT_OWNER_CHANGED', ownerChangedLink)}
            {panel.createRadioWithContentNoSubmit('REPORT_PHOTO', reportPhotoText)}
            {panel.createRadioWithContentNoSubmit('REMOVE_LISTING', removeListingLink)}
          </React.Fragment>
        );
      }}
    </ReportIAPPanel>
  );
};

const renderNonOwnerFlow = (
  props: InternalReviewProps,
  locationId: number,
  isNonHotel: boolean,
) => {
  const updateListingText =
    locationId &&
    unescaped(localize('social_report_level2_redirect', { sUrl: `/UpdateListing-d${locationId}` }));

  const factCheck =
    locationId && unescaped(localize('social_report_level2_1', { sUrl: `/Owners-d${locationId}` }));

  return (
    <ReportIAPPanel
      csrfToken={props.csrfToken}
      userId={props.userId}
      // $FlowFixMe contentId is fine, I'm not sure what you're so mad about.
      contentId={props.contentId}
      contentType={ContentType.USERREVIEW}
      onError={props.onError}
      onComplete={props.onComplete}
      repostInfo={props.repostInfo}
    >
      {panel => (
        <React.Fragment>
          {panel.createRadioWithOptionalComment('PROFANE_EXPLICIT_HATE_SPEECH')}
          {panel.createRadioWithOptionalComment('PROMOTES_ILLEGAL_ACTIVITY')}
          {panel.createRadioWithRequiredComment('BIASED')}

          {isNonHotel
            ? panel.createRadioWithContentNoSubmit('CLOSED', <span>{updateListingText}</span>)
            : panel.createRadioWithRequiredComment('CLOSED')}

          {panel.createRadioWithSuggestionBox(
            'WRONG_LOCATION',
            localize('report_review_radio_desc_19'),
          )}
          {panel.createRadioWithOptionalComment('DUPLICATE_CONTENT')}
          {panel.createRadioWithOptionalComment('NOT_PERSONAL_EXPERIENCE')}
          {panel.createRadioWithContentNoSubmit('FALSE_MISLEADING_INFO', <span>{factCheck}</span>)}
          {panel.createRadioWithRequiredComment('OTHER')}
        </React.Fragment>
      )}
    </ReportIAPPanel>
  );
};

const ReportReviewPanel = (props: InternalReviewProps) => {
  const { placeType = '', locationId = 1 } = props.location || {};
  const isNonHotel = placeType === 'ATTRACTION' || placeType === 'EATERY';

  return (
    <Query query={IsOwnerQuery} variables={{ locationId }}>
      {({ fetching, data }) => {
        if (fetching) {
          return null;
        }
        // $FlowFixGraphql
        const location = data?.locations && data.locations[0];

        // Owner flow
        if (location && location.currentUserOwnerStatus?.isValid) {
          return renderOwnerFlow(props, location, locationId, isNonHotel);
        }

        // Non-owner flow
        return renderNonOwnerFlow(props, locationId, isNonHotel);
      }}
    </Query>
  );
};
ReportReviewPanel.defaultProps = DefaultProps;

// $FlowFixMe92
export default connect(state => defaultConnect(state))(ReportReviewPanel);
